import React, { useEffect, useState } from 'react';
import { pwa } from '@/data/content';
import Button from '@/components/ui/Button';

const STORAGE_KEY = 'pwa_install_prompt_dismissed';

interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

const InstallAppPrompt: React.FC = () => {
    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        let dismissed = false;
        try {
            dismissed = window.localStorage.getItem(STORAGE_KEY) === '1';
        } catch {
            /* incognito/private mode */
        }
        if (dismissed) {
            return undefined;
        }

        const onBeforeInstallPrompt = (event: Event) => {
            const e = event as BeforeInstallPromptEvent;
            e.preventDefault();
            setDeferredPrompt(e);
            setVisible(true);
        };

        window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);

        return () => {
            window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
        };
    }, []);

    if (!visible) {
        return null;
    }

    const closePrompt = () => {
        try {
            window.localStorage.setItem(STORAGE_KEY, '1');
        } catch {
            /* incognito/private mode */
        }
        setVisible(false);
    };

    const install = async () => {
        if (!deferredPrompt) {
            closePrompt();
            return;
        }

        deferredPrompt.prompt();
        await deferredPrompt.userChoice;
        closePrompt();
    };

    return (
        <div className="fixed bottom-24 left-3 right-3 md:left-auto md:right-4 md:bottom-24 md:max-w-sm z-50 animate-in slide-in-from-bottom-5 fade-in duration-300">
            <div className="bg-app-surface border border-app-border rounded-2xl p-4 shadow-xl flex flex-col gap-2">
                <div className="flex items-start justify-between">
                    <p className="font-bold text-app-text m-0 leading-tight">{pwa.title}</p>
                    <Button
                        variant="ghost"
                        type="button"
                        aria-label="Fermer"
                        onClick={closePrompt}
                        className="text-app-muted hover:text-app-text p-1 -mr-1 -mt-1 rounded-md transition-colors"
                    >
                        ✕
                    </Button>
                </div>
                <p className="text-sm text-app-muted m-0">{pwa.description}</p>
                <div className="flex items-center gap-2 mt-2">
                    <Button variant="primary" size="sm" fullWidth onClick={install}>
                        {pwa.install_label}
                    </Button>
                    <Button variant="secondary" size="sm" fullWidth onClick={closePrompt}>
                        {pwa.later_label}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default InstallAppPrompt;
